import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { mapboxgl, MAPBOX_STYLE, MAPBOX_TOKEN, BRAND_BLUE } from "@/lib/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { Layout } from "@/components/Layout";
import api, { formatApiError, formatDate, formatMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Truck, MapPin, Fire, Package, CreditCard, Warning, SealCheck } from "@phosphor-icons/react";

const SIZES = [
  { v: "3mm", l: "3mm (fine — delicate surfaces)" },
  { v: "9mm", l: "9mm (general purpose)" },
  { v: "16mm", l: "16mm (aggressive)" },
];

function OrderForm({ location, onSubmitted }) {
  const [form, setForm] = useState({
    pellet_size: "9mm", pounds: 500, delivery_method: "pickup", delivery_address: "",
    pickup_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const selectedPrice = (location.prices || []).find((p) => p.pellet_size === form.pellet_size);
  const subtotal = (Number(form.pounds) || 0) * (selectedPrice?.member_lb || 0);
  const deliveryFee = form.delivery_method === "delivery" ? (location.delivery_fee || 0) : 0;
  const estTotal = subtotal + deliveryFee;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/pellet/orders", { location_id: location.id, ...form, pounds: Number(form.pounds) });
      toast.success("Order submitted — admin will send a payment link once supplier confirms");
      onSubmitted?.();
    } catch (err) { toast.error(formatApiError(err)); }
    setSubmitting(false);
  };

  return (
    <form onSubmit={submit} className="surface p-6 space-y-4" data-testid={`pellet-order-form-${location.id}`}>
      <div className="section-label">— ORDER FROM {location.name.toUpperCase()}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Pellet size</label>
          <select value={form.pellet_size} onChange={(e) => set("pellet_size", e.target.value)} className="input-rugged" data-testid="pellet-size">
            {SIZES.map((s) => {
              const price = (location.prices || []).find((p) => p.pellet_size === s.v);
              return <option key={s.v} value={s.v} disabled={!price}>
                {s.l} {price ? `· ${formatMoney(price.member_lb)}/lb · ${price.stock}` : "· not stocked"}
              </option>;
            })}
          </select>
        </div>
        <div>
          <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Pounds</label>
          <input type="number" min={location.min_order_lbs || 50} max={location.max_order_lbs || 5000}
                 step={location.step_lbs || 50} value={form.pounds}
                 onChange={(e) => set("pounds", e.target.value)} className="input-rugged" data-testid="pellet-pounds" />
          <div className="font-mono text-[10px] text-[#52525B] mt-1">Min {location.min_order_lbs} · Max {location.max_order_lbs} · Step {location.step_lbs}</div>
        </div>
        <div>
          <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Fulfilment</label>
          <select value={form.delivery_method} onChange={(e) => set("delivery_method", e.target.value)} className="input-rugged" data-testid="pellet-delivery-method">
            {location.pickup && <option value="pickup">Pickup</option>}
            {location.delivery && <option value="delivery">Delivery</option>}
          </select>
        </div>
        <div>
          <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Pickup / delivery date</label>
          <input type="date" value={form.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} className="input-rugged" data-testid="pellet-date" />
        </div>
      </div>

      {form.delivery_method === "delivery" && (
        <div>
          <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Delivery address</label>
          <input value={form.delivery_address} onChange={(e) => set("delivery_address", e.target.value)} required placeholder="Full street address" className="input-rugged" data-testid="pellet-delivery-address" />
        </div>
      )}

      <div>
        <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Notes (optional)</label>
        <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="input-rugged" data-testid="pellet-notes" />
      </div>

      <div className="border-t border-[#262626] pt-4 grid grid-cols-3 gap-4 text-sm">
        <div><div className="font-mono text-[10px] text-[#52525B] uppercase">Pellet subtotal</div><div className="font-chivo font-bold text-white">{formatMoney(subtotal)}</div></div>
        <div><div className="font-mono text-[10px] text-[#52525B] uppercase">Delivery fee</div><div className="font-chivo font-bold text-white">{formatMoney(deliveryFee)}</div></div>
        <div><div className="font-mono text-[10px] text-[#52525B] uppercase">Estimated total</div><div className="font-chivo font-black text-[#00D4FF] text-xl">{formatMoney(estTotal)}</div></div>
      </div>

      <div className="font-mono text-[10px] text-[#52525B]">Submitting does not charge your card. Admin confirms supplier availability then emails you a secure Stripe payment link.</div>

      <button type="submit" disabled={submitting || !selectedPrice} className="btn-primary w-full" data-testid="pellet-submit-order">
        <Package size={14} weight="bold" /> {submitting ? "Submitting..." : "Submit Order Request"}
      </button>
    </form>
  );
}

export default function Pellets() {
  const { user } = useAuth();
  const [zip, setZip] = useState("");
  const [data, setData] = useState({ locations: [], origin: null });
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  const load = async (z = "") => {
    setLoading(true);
    try {
      const qp = z ? `?zip=${z}&radius=500` : "";
      const r = await api.get(`/pellet/locations${qp}`);
      setData(r.data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };
  const loadOrders = async () => {
    try { const r = await api.get("/me/pellet-orders"); setOrders(r.data); } catch {}
  };

  useEffect(() => { load(); loadOrders(); }, []);

  // Mapbox
  useEffect(() => {
    if (!mapContainerRef.current || !MAPBOX_TOKEN) return;
    if (!mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: MAPBOX_STYLE,
        center: [-98.5795, 39.8283],
        zoom: 3.5,
        attributionControl: false,
      });
      mapRef.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    }
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    (data.locations || []).forEach((loc) => {
      if (!loc.lat || !loc.lng) return;
      const el = document.createElement("div");
      el.style.cssText = `width:22px;height:22px;border-radius:4px;background:${BRAND_BLUE};border:3px solid #0A0A0A;box-shadow:0 0 0 2px ${BRAND_BLUE},0 4px 10px rgba(0,0,0,.5);cursor:pointer`;
      el.setAttribute("data-testid", `pellet-map-pin-${loc.id}`);
      el.onclick = () => setSelectedLocation(loc);
      const popup = new mapboxgl.Popup({ offset: 16, closeButton: false }).setHTML(
        `<div style="font-family:sans-serif;color:#000"><strong>${loc.name}</strong><br/><span style="font-size:11px;color:#525252">${loc.city}, ${loc.state}${loc.distance_mi ? ' · ' + loc.distance_mi + ' mi' : ''}</span></div>`
      );
      const m = new mapboxgl.Marker({ element: el }).setLngLat([loc.lng, loc.lat]).setPopup(popup).addTo(mapRef.current);
      markersRef.current.push(m);
    });
    if (data.origin?.lat) mapRef.current.flyTo({ center: [data.origin.lng, data.origin.lat], zoom: 7 });
  }, [data]);

  if (user && (user.level || 0) < 1) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="pellets-level-gate">
          <div className="section-label mb-3">— MEMBERS ONLY</div>
          <h1 className="font-chivo font-black text-4xl text-white uppercase tracking-tight">Pellet pricing is a member benefit</h1>
          <p className="font-plex text-[#9CA3AF] mt-4">Activate your membership to see pellet locations, member pricing, and request orders.</p>
          <Link to="/pricing" className="btn-primary mt-6 inline-flex" data-testid="pellets-upgrade-cta">Choose a Plan</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <div className="section-label mb-2">— PELLET NETWORK</div>
            <h1 className="font-chivo font-black text-4xl lg:text-5xl text-white uppercase tracking-tight" data-testid="pellets-headline">
              Order <span className="text-[#00D4FF]">member-price</span> pellets
            </h1>
            <p className="font-plex text-sm text-[#9CA3AF] mt-2 max-w-2xl">
              Dry Ice Connect brokers every order: you submit, admin confirms supplier availability, you get a Stripe payment link. You never pay the supplier directly.
            </p>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); load(zip); }} className="flex items-center gap-2" data-testid="pellets-search-form">
            <div className="input-rugged flex items-center gap-2 py-3"><MapPin size={14} weight="duotone" className="text-[#00D4FF]" />
              <input inputMode="numeric" pattern="[0-9]*" maxLength={5} value={zip}
                     onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
                     placeholder="Your zip" className="bg-transparent outline-none text-white w-28" data-testid="pellets-zip-input" />
            </div>
            <button type="submit" className="btn-primary" data-testid="pellets-search-btn">Find</button>
          </form>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 h-[460px] border border-[#262626]" data-testid="pellets-map">
            <div ref={mapContainerRef} style={{ width: "100%", height: "100%", background: "#0A0A0A" }} />
          </div>

          <div className="lg:col-span-5 space-y-3" data-testid="pellets-locations">
            {loading ? <div className="font-mono text-xs text-[#52525B] text-center py-8">[LOADING]</div>
              : data.locations.length === 0 ? (
                <div className="surface p-8 text-center">
                  <Fire size={36} weight="duotone" className="text-[#00D4FF] mx-auto mb-2" />
                  <div className="font-chivo font-bold text-white uppercase">No pellet depots {zip ? `near ${zip}` : "yet"}</div>
                  <p className="font-plex text-sm text-[#9CA3AF] mt-2">New suppliers are onboarding. Check back soon or <a className="text-[#00D4FF]" href="mailto:support@dryiceconnect.com">tell us where you need coverage</a>.</p>
                </div>
              ) : data.locations.map((loc) => (
                <div key={loc.id} className={`surface p-5 ${selectedLocation?.id === loc.id ? "border-[#00D4FF]" : ""}`} data-testid={`pellet-location-${loc.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-chivo font-bold text-white uppercase tracking-tight truncate">{loc.name}</div>
                      <div className="font-mono text-xs text-[#52525B]">{loc.city}, {loc.state}{loc.distance_mi ? ` · ${loc.distance_mi} mi` : ""}</div>
                    </div>
                    <div className="flex gap-1">
                      {loc.pickup && <span className="tag">PICKUP</span>}
                      {loc.delivery && <span className="tag">DELIVERY</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 font-mono text-[11px] text-[#9CA3AF]">
                    {(loc.prices || []).map((p) => (
                      <span key={p.id || p.pellet_size}>
                        {p.pellet_size}: <span className="text-white">{formatMoney(p.member_lb)}/lb</span>
                        {p.stock !== "in_stock" && <span className="text-[#FF5722]"> ({p.stock})</span>}
                      </span>
                    ))}
                  </div>
                  <button onClick={() => setSelectedLocation(loc)} className="btn-primary text-xs mt-3 w-full" data-testid={`pellet-order-cta-${loc.id}`}>
                    <Truck size={12} weight="bold" /> Request order
                  </button>
                </div>
              ))}
          </div>
        </div>

        {selectedLocation && (
          <div className="mt-8" data-testid="pellet-order-section">
            <OrderForm location={selectedLocation} onSubmitted={() => { setSelectedLocation(null); loadOrders(); }} />
          </div>
        )}

        {/* My Orders */}
        <div className="mt-10">
          <div className="section-label mb-3">— MY PELLET ORDERS</div>
          {orders.length === 0 ? (
            <div className="surface p-8 text-center font-mono text-xs text-[#52525B]" data-testid="pellet-orders-empty">No orders yet.</div>
          ) : (
            <div className="space-y-3" data-testid="pellet-orders-list">
              {orders.map((o) => (
                <div key={o.id} className="surface p-5 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`pellet-order-${o.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-chivo font-bold text-white uppercase">#{o.id.slice(-6)}</span>
                      <OrderStatusTag status={o.status} />
                    </div>
                    <div className="font-mono text-xs text-[#52525B]">
                      {formatDate(o.created_at)} · {o.pounds} lbs {o.pellet_size} · {o.delivery_method} · pickup {o.pickup_date}
                    </div>
                    <div className="font-mono text-xs text-[#9CA3AF] mt-1">
                      Est. total <span className="text-white">{formatMoney(o.estimated_total)}</span>
                    </div>
                  </div>
                  {o.stripe_payment_url && o.status === "invoiced" && (
                    <a href={o.stripe_payment_url} className="btn-primary text-xs" data-testid={`pellet-pay-${o.id}`}>
                      <CreditCard size={12} weight="bold" /> Pay now
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function OrderStatusTag({ status }) {
  const map = {
    awaiting_supplier: { label: "AWAITING SUPPLIER", cls: "" },
    confirmed: { label: "CONFIRMED", cls: "tag-active" },
    invoiced: { label: "PAYMENT READY", cls: "tag-active" },
    paid: { label: "PAID", cls: "tag-active" },
    cancelled: { label: "CANCELLED", cls: "" },
    new: { label: "NEW", cls: "" },
  };
  const m = map[status] || { label: status.toUpperCase(), cls: "" };
  return <span className={`tag ${m.cls}`}>{m.label}</span>;
}
